import { isEqual } from 'lodash'
import type { AgentSettingsFormValues } from '../config/base.config'
import { useFlowStore } from '../store'
import type { FlowData } from '../types'
import { compareFlowChanges } from './index'

// Settings fields the playground save payload actually persists (see usePublishFlow)
const SETTINGS_FIELDS = [
  'voice_ai_config',
  'schedule',
  'event_callbacks',
  'knowledge_base_ids',
  'conversation_goals',
] as const

/**
 * True only when a playground save would be a no-op — the saved flow already
 * matches the canvas, mounted tab forms, and live settings. Lets "Test agent"
 * skip the republish, which would otherwise mint a new phlo version (and a fresh
 * set of node rows) backend-side on every run.
 * Fail-open polarity: any doubt or error returns false (i.e. save as usual).
 */
export const isFlowCleanForPlayground = (
  flowData: FlowData | undefined,
  liveSettings: AgentSettingsFormValues | null,
): boolean => {
  try {
    if (!flowData?.phlo?.id) return false
    // Stored validation errors must re-surface through the save path
    if (Array.isArray(flowData.validation_errors) && flowData.validation_errors.length > 0) {
      return false
    }

    const { flowInstance, currentFlowType, tabFormInstances, initialAgentSettings } =
      useFlowStore.getState()
    if (!flowInstance || !currentFlowType) return false

    // Unsubmitted edits in mounted tab forms (settings / voice config / callbacks)
    const tabFormsDirty = Object.values(tabFormInstances).some((form) => {
      if (!form) return false
      const defaults =
        typeof form.options.defaultValues === 'function'
          ? form.options.defaultValues()
          : form.options.defaultValues
      return !isEqual(form.state.values, defaults)
    })
    if (tabFormsDirty) return false

    // Live settings (what publishFlow would save) vs last-saved snapshot
    const settingsDirty = SETTINGS_FIELDS.some(
      (key) => !isEqual(liveSettings?.[key] ?? null, initialAgentSettings?.[key] ?? null),
    )
    if (settingsDirty) return false

    // Graph diff in stable-id space; rethrow so transform failures mean "save"
    return !compareFlowChanges(
      flowInstance.getNodes(),
      flowInstance.getEdges(),
      currentFlowType,
      flowData,
      flowData.phlo.id,
      { rethrowOnError: true },
    )
  } catch {
    return false
  }
}
